import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useStore } from '@thd-nucleus/experience-context';
import {
  Alert,
  Skeleton,
  SkeletonBlock,
  SkeletonLine
} from '@one-thd/sui-atomic-components';
import { partsAndServicesDataModel } from './partsAndServicesDataModel';
import { PartsAndServicesProvider } from './PartsAndServicesProvider';
import { Header } from './subcomponents/Header';
import { Protection } from './subcomponents/Protection';
import { Services } from './subcomponents/Services';
import { Confirmation } from './subcomponents/Confirmation';
import { Total } from './subcomponents/Total';
import { Buttons } from './subcomponents/Buttons';
import { PayPalMessage } from './subcomponents/PayPalMessage';
import {
  DEFAULT_QUERY_VARIABLES,
  PAGE_TYPE,
  RELATIVE_CART_URL,
  POST_MESSAGE
} from '../constants';
import {
  updateCart,
  getAddOnsFromCartModel,
  getFulfillmentSelections,
  postMessageToParent,
  determinePageConfiguration
} from '../utils';

export const PartsAndServices = ({
  addOns,
  addOnsSelections: addOnsSelectionsProp,
  applianceStoreId,
  deliveryZip,
  disableHeader,
  disableNavigation,
  enableInstallationFirst,
  enableCheckoutEvent,
  exclusionListProtection,
  exclusionListServices,
  fulfillment,
  fulfillmentSelections: fulfillmentSelectionsProp,
  itemIds,
  lineItemIds,
  onSubmit,
  pageType,
  showAlert,
  showZipCodeAlert,
  onClose,
  showUpdatedZipCodeAlert,
  typeOfSelection,
  disableUpdateCart,
  hasDropDownSelections,
  cartResponse,
  confirmationEnabled = false,
  fullViewportHeight = false
}) => {
  // fallback for if deliveryZip or applianceDeliveryStoreId are missing
  const {
    storeId = DEFAULT_QUERY_VARIABLES.storeId,
    storeZip = DEFAULT_QUERY_VARIABLES.storeZip
  } = useStore();

  const cartItems = useMemo(
    () => lineItemIds.map((lineItemId, index) => ({
      id: lineItemId,
      selectedFulfillment: fulfillment?.[index] ?? fulfillment?.[0] ?? 'direct delivery',
      product: {
        addons: addOns?.length ? addOns[index] : []
      }
    })),
    [lineItemIds, fulfillment, addOns]
  );

  const hasItems = cartItems?.length > 0;

  const derivedAddOnsSelections = useMemo(
    () => (hasItems ? getAddOnsFromCartModel({ items: cartItems }) : {}),
    [hasItems, cartItems]
  );
  const derivedFulfillmentSelections = useMemo(
    () => (hasItems ? getFulfillmentSelections({ items: cartItems }) : {}),
    [hasItems, cartItems]
  );

  const myAddOnsSelections = addOnsSelectionsProp ?? derivedAddOnsSelections;
  const myFulfillmentSelections = fulfillmentSelectionsProp ?? derivedFulfillmentSelections;

  useEffect(() => {
    postMessageToParent({ message: POST_MESSAGE.OPENED });
  }, []);

  const [currentPage, setCurrentPage] = useState(!confirmationEnabled ? (pageType || 'protection') : null);
  const isProtection = currentPage === PAGE_TYPE.PROTECTION;
  const isServices = currentPage === PAGE_TYPE.SERVICES || currentPage === PAGE_TYPE.HAUL_AWAY;
  const isConfirmation = currentPage === PAGE_TYPE.CONFIRMATION;
  const isSelectionDrawer = isProtection || isServices;
  const isLoading = !isProtection && !isServices && !isConfirmation;

  const navigateToCart = () => {
    return window?.open(window.location?.origin + RELATIVE_CART_URL, '_top');
  };

  const navigateToProtection = () => {
    setCurrentPage(PAGE_TYPE.PROTECTION);
  };

  const navigateToServices = () => {
    setCurrentPage(PAGE_TYPE.SERVICES);
  };

  const [cartDirectData, setCartDirectData] = useState(cartResponse);

  const navigateToConfirmation = (response = null) => {
    if (response) {
      setCartDirectData(response);
    }
    setCurrentPage(PAGE_TYPE.CONFIRMATION);
  };

  const CTA_OPTIONS = useMemo(() => ({
    continueToCart: {
      buttonText: 'Continue to Cart',
      buttonVariant: 'primary',
      shouldPostSelectionsToCart: true,
      onSubmit: () => {
        updateCart();
        navigateToCart();
      },
      href: RELATIVE_CART_URL
    },
    continueToCheckout: {
      buttonText: 'Continue to Checkout',
      buttonVariant: 'primary',
      shouldPostSelectionsToCart: true,
      onSubmit: () => { onSubmit({ action: 'checkout' }); }
    },
    continueToInstallation: {
      buttonText: 'Continue to Installation',
      buttonVariant: 'primary',
      shouldPostSelectionsToCart: disableNavigation,
      onSubmit: () => {
        navigateToServices();
        onSubmit({ action: 'continue' });
      }
    },
    continueToProtection: {
      buttonText: 'Continue to Coverage',
      buttonVariant: 'primary',
      shouldPostSelectionsToCart: disableNavigation,
      onSubmit: () => {
        navigateToProtection();
        onSubmit({ action: 'continue' });
      }
    },
    save: {
      buttonText: 'Save',
      buttonVariant: 'primary',
      shouldPostSelectionsToCart: true,
      isProtection,
      onSubmit: () => {
        // TODO Once Iframe is gone, use onSubmit callback and remove postmessage
        postMessageToParent({ message: POST_MESSAGE.CLOSED });
        if (onClose) {
          onClose();
        }
        onSubmit({ action: 'save' });
      },
      onSubmitClick: () => {
        onSubmit({ action: 'click' });
      }
    },
    saveAndKeepShopping: {
      buttonText: 'Save & Keep Shopping',
      buttonVariant: disableUpdateCart ? 'primary' : 'secondary',
      shouldPostSelectionsToCart: !disableUpdateCart,
      // TODO Once Iframe is gone, use onSubmit callback and remove postmessage
      onSubmit: () => {
        updateCart();
        postMessageToParent({ message: POST_MESSAGE.CLOSED });
        if (onClose) {
          onClose();
        }
      },
      onSubmitClick: onSubmit,
    },
    keepShopping: {
      buttonText: 'Keep Shopping',
      buttonVariant: 'secondary',
      shouldPostSelectionsToCart: false,
      // TODO Once Iframe is gone, use onSubmit callback and remove postmessage
      onSubmit: () => {
        // updateCart();
        postMessageToParent({ message: POST_MESSAGE.CLOSED });
        if (onClose) {
          onClose();
        }
      }
    },
    returnToCartNoUpdate: {
      buttonText: 'Return to Cart',
      buttonVariant: 'primary',
      shouldPostSelectionsToCart: false,
      // TODO Once Iframe is gone, use onSubmit callback and remove postmessage
      onSubmit: () => {
        // updateCart();
        postMessageToParent({ message: POST_MESSAGE.CLOSED });
        if (onClose) {
          onClose();
        }
      }
    },
    viewCart: {
      buttonText: 'View Cart',
      buttonVariant: 'primary',
      shouldPostSelectionsToCart: false,
      onSubmit: () => {
        navigateToCart();
      },
      href: RELATIVE_CART_URL
    }
  }), [disableNavigation, disableUpdateCart, isProtection, onClose, onSubmit]);

  const hasServicesToShow = useMemo(
    () => !!lineItemIds.filter((lineItemId) => !exclusionListServices.includes(lineItemId)).length,
    [lineItemIds, exclusionListServices]
  );
  const hasProtectionsToShow = useMemo(
    () => !!lineItemIds.filter((lineItemId) => !exclusionListProtection.includes(lineItemId)).length,
    [lineItemIds, exclusionListProtection]
  );

  const pageConfig = useMemo(
    () => determinePageConfiguration({
      enableCheckoutEvent,
      isProtection,
      hasServicesToShow,
      hasProtectionsToShow,
      disableNavigation,
      disableUpdateCart,
      enableInstallationFirst,
      showAlert,
      hasDropDownSelections,
      confirmationEnabled,
      isConfirmation
    }),
    [
      enableCheckoutEvent,
      isProtection,
      hasServicesToShow,
      hasProtectionsToShow,
      disableNavigation,
      disableUpdateCart,
      enableInstallationFirst,
      showAlert,
      hasDropDownSelections,
      confirmationEnabled,
      isConfirmation
    ]
  );

  const ctaButtons = pageConfig.ctaButtonKeys.map((key) => CTA_OPTIONS[key]);

  let onBack = null;
  if (pageConfig.onBack === 'navigateToServices') {
    onBack = navigateToServices;
  } else if (pageConfig.onBack === 'navigateToProtection') {
    onBack = navigateToProtection;
  }

  const {
    headerText,
    showSuccessCheck,
    isCheckoutFlow,
    addDirectlyToCart
  } = pageConfig;

  useEffect(() => {
    if (enableCheckoutEvent && !hasProtectionsToShow && isProtection) {
      setCurrentPage('services');
    }
  }, [enableCheckoutEvent, hasProtectionsToShow, isProtection]);

  // TODO Remove once iFrame is totally removed
  const pageClassNames = classnames(
    'sui-flex sui-flex-col sui-flex-1 sui-h-full',
    {
      'sui-pt-24': onBack,
      'sui-pt-16': !onBack,
      'sui-pl-4 sui-pr-4': !disableHeader
    }
  );

  const rootClassNames = classnames(
    'sui-flex sui-flex-col sui-h-full',
    { 'sui-min-h-screen': fullViewportHeight }
  );

  return (
    <div data-component="PartsAndServices" className={rootClassNames}>
      <PartsAndServicesProvider
        applianceStoreId={applianceStoreId || storeId}
        addOnsSelections={myAddOnsSelections}
        fulfillmentSelections={myFulfillmentSelections}
        currentPage={currentPage}
        typeOfSelection={typeOfSelection}
        disableHeader={disableHeader}
        disableNavigation={disableNavigation}
        enableCheckoutEvent={enableCheckoutEvent}
        itemIds={itemIds}
        lineItemIds={lineItemIds}
        deliveryZip={deliveryZip || storeZip}
        isCheckoutFlow={isCheckoutFlow}
        enableInstallationFirst={enableInstallationFirst}
        addDirectlyToCart={addDirectlyToCart}
        confirmationEnabled={confirmationEnabled}
        pageNavigation={{
          navigateToCart,
          navigateToProtection,
          navigateToServices,
          navigateToConfirmation
        }}
      >
        {(!disableHeader && !isLoading) && (
          <Header
            headerText={headerText}
            onBack={onBack}
            onClose={onClose}
            showSuccessCheck={showSuccessCheck}
          />
        )}
        <div className={pageClassNames}>
          {showUpdatedZipCodeAlert && (
            <div className="sui-mt-4">
              <Alert status="success" size="large">
                Your ZIP Code has been updated to {deliveryZip}.
              </Alert>
            </div>
          )}
          {isLoading && (
            <Skeleton disablePadding grow>
              <SkeletonBlock />
              <SkeletonLine variant="multi" numberOfLines={12} />
            </Skeleton>
          )}
          {isProtection && (
            <Protection
              exclusionListProtection={exclusionListProtection}
              hideTitle={disableNavigation}
              showAlert={showAlert}
              showZipCodeAlert={showZipCodeAlert}
            />
          )}
          {isServices && (
            <Services
              exclusionListServices={exclusionListServices}
              hideTitle={disableNavigation}
              showAlert={showAlert}
            />
          )}
          {isConfirmation && (
            <Confirmation
              lineItemIds={lineItemIds}
              cartDirectData={cartDirectData}
            />
          )}
          <div className="sui-mt-auto">
            {isSelectionDrawer && (
              <>
                <hr className="sui-my-4" />
                {!disableNavigation && (
                  <div>
                    <Total />
                  </div>
                )}
              </>
            )}
            <Buttons ctaButtons={ctaButtons} ctaOptions={CTA_OPTIONS} isProtection={isProtection} />
            {isSelectionDrawer && (
              <PayPalMessage />
            )}
          </div>
        </div>
      </PartsAndServicesProvider>
    </div>
  );
};

PartsAndServices.propTypes = {
  addOns: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string,
    configAttr: PropTypes.string,
    type: PropTypes.string
  })),
  applianceStoreId: PropTypes.string,
  deliveryZip: PropTypes.string,
  disableHeader: PropTypes.bool,
  disableNavigation: PropTypes.bool,
  enableCheckoutEvent: PropTypes.bool,
  enableInstallationFirst: PropTypes.bool,
  exclusionListProtection: PropTypes.arrayOf(PropTypes.string),
  exclusionListServices: PropTypes.arrayOf(PropTypes.string),
  fulfillment: PropTypes.arrayOf(PropTypes.string),
  lineItemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  typeOfSelection: PropTypes.string,
  itemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  pageType: PropTypes.oneOf(['services', 'protection', 'haulAway', 'confirmation']),
  showAlert: PropTypes.bool,
  showZipCodeAlert: PropTypes.bool,
  showUpdatedZipCodeAlert: PropTypes.bool,
  disableUpdateCart: PropTypes.bool,
  hasDropDownSelections: PropTypes.bool,
  cartResponseProp: PropTypes.object,
  confirmationEnabled: PropTypes.bool,
  fullViewportHeight: PropTypes.bool
};

PartsAndServices.defaultProps = {
  addOns: [],
  addOnsSelections: null,
  applianceStoreId: '',
  deliveryZip: '',
  disableHeader: false,
  disableNavigation: false,
  enableCheckoutEvent: false,
  enableInstallationFirst: false,
  exclusionListProtection: [],
  exclusionListServices: [],
  fulfillment: ['direct delivery'],
  fulfillmentSelections: null,
  onClose: null,
  onSubmit: () => { },
  pageType: null,
  showAlert: false,
  showZipCodeAlert: false,
  showUpdatedZipCodeAlert: false,
  typeOfSelection: '',
  disableUpdateCart: false,
  hasDropDownSelections: false,
  cartResponse: null,
  confirmationEnabled: false,
  fullViewportHeight: false
};

PartsAndServices.dataModel = partsAndServicesDataModel;
PartsAndServices.displayName = 'PartsAndServices';
